import Sortable from "sortablejs";
import { fetchWithOOB } from "./htmx-helpers";
import { computeReorder, type MoveTarget } from "./asset-reorder";
import { alpineData } from "./alpine-data";
import { studioConfirm } from "./studio-confirm";

/**
 * Initialize SortableJS on the manage-page group list. Exposed on window so the
 * deliverable_sidebar.templ x-init can invoke it without needing Sortable
 * available as a global (the inline templ JS cannot import modules).
 *
 * The element's surrounding x-data scope provides `commitGroupOrder(orderedIds)`,
 * which we call via Alpine's evaluation on drag-end.
 */
function initDeliverableGroupSortable(el: HTMLElement) {
  const existing = (el as any)._sortable;
  if (existing) existing.destroy();
  (el as any)._sortable = Sortable.create(el, {
    handle: ".group-drag-handle",
    animation: 150,
    onEnd: () => {
      const ids = Array.from(el.querySelectorAll("[data-group-id]"))
        .map((node) => (node as HTMLElement).dataset.groupId)
        .filter((v): v is string => !!v);
      const scope = (window as any).Alpine?.$data(el);
      if (scope && typeof scope.commitGroupOrder === "function") {
        scope.commitGroupOrder(ids);
      }
    },
  });
}
(window as any).initDeliverableGroupSortable = initDeliverableGroupSortable;

export const registerDeliverableManager = (Alpine: any) => {
  Alpine.data(
    "deliverableManager",
    (initialGroupID: string, deleteConfirmMsg: string, deliverableID: string) => alpineData({
      activeGroupID: initialGroupID,
      deliverableID: deliverableID,
      deleteConfirmMsg: deleteConfirmMsg,
      sortMode: false,
      orderDirty: false,
      assetOrder: [] as any[],
      originalOrder: [] as any[],
      selectedSortIds: [] as string[],
      sortAnchorId: null as string | null,
      _sortPointerDown: false,
      _suppressFocusSelect: false,
      renameAssetID: null as string | null,
      renameAssetFilename: "",
      renameAssetExtension: "",
      _uploadRefreshTimer: 0 as any,
      _pendingUploadedGroups: null as Set<string> | null,

      init() {
        // Listen for group selection from sidebar
        this.$watch("activeGroupID", (id: string) => {
          if (id) {
            this.refreshContent();
          }
        });
      },

      // Called from @asset-uploaded.document on the page root. The server emits
      // this trigger after a successful confirm-upload regardless of which page
      // the user is on; the filter below guarantees only the matching deliverable
      // refreshes, and batch uploads are coalesced into a single sidebar+content
      // refresh per ~150ms burst.
      onAssetUploaded(detail: { deliverableID?: string; groupID?: string }) {
        if (!detail || detail.deliverableID !== this.deliverableID) return;

        if (!this._pendingUploadedGroups) {
          this._pendingUploadedGroups = new Set<string>();
        }
        if (detail.groupID) {
          this._pendingUploadedGroups.add(detail.groupID);
        }

        clearTimeout(this._uploadRefreshTimer);
        this._uploadRefreshTimer = setTimeout(() => {
          const groups = this._pendingUploadedGroups || new Set<string>();
          this._pendingUploadedGroups = null;

          // Sidebar carries the per-group asset counters; refresh once for
          // any number of completed uploads.
          // @ts-ignore - htmx is global from CDN
          if (window.htmx) {
            // @ts-ignore
            window.htmx.trigger("#deliverable-sidebar-container", "refresh");
          }

          // Only re-fetch the visible group's content if at least one of the
          // completed uploads landed in it. Other groups update next time the
          // user clicks them (the sidebar already shows the new count).
          if (this.activeGroupID && groups.has(this.activeGroupID)) {
            this.refreshContent();
          }
        }, 150);
      },

      enterSortMode() {
        this.sortMode = true;
        this.orderDirty = false;
        this.selectedSortIds = [];
        this.sortAnchorId = null;
        const container = document.getElementById("group-content-" + this.activeGroupID);
        if (!container) return;

        this.assetOrder = Array.from(container.querySelectorAll("[data-asset-wrapper]")).map(
          (el) => ({ id: (el as HTMLElement).dataset.assetWrapper as string }),
        );
        this.originalOrder = JSON.parse(JSON.stringify(this.assetOrder));
      },

      exitSortMode(saved = false) {
        this.sortMode = false;
        this.orderDirty = false;
        this.selectedSortIds = [];
        this.sortAnchorId = null;
        if (!saved) {
          this.assetOrder = JSON.parse(JSON.stringify(this.originalOrder));
          this.updateDOM();
        }
      },

      selectSingle(id: string) {
        this.selectedSortIds = [id];
        this.sortAnchorId = id;
      },

      toggleSelect(id: string) {
        const i = this.selectedSortIds.indexOf(id);
        if (i === -1) {
          this.selectedSortIds = [...this.selectedSortIds, id];
        } else {
          this.selectedSortIds = this.selectedSortIds.filter((x) => x !== id);
        }
        this.sortAnchorId = id;
      },

      rangeSelect(id: string) {
        const ids = this.assetOrder.map((a) => a.id);
        const anchor = this.sortAnchorId ?? id;
        const a = ids.indexOf(anchor);
        const b = ids.indexOf(id);
        if (a === -1 || b === -1) {
          this.selectSingle(id);
          return;
        }
        const [lo, hi] = a <= b ? [a, b] : [b, a];
        this.selectedSortIds = ids.slice(lo, hi + 1);
        // anchor unchanged, so a subsequent shift-click re-ranges from it
      },

      onSortClick(event: MouseEvent, id: string) {
        this._sortPointerDown = false;
        if (event.shiftKey) {
          this.rangeSelect(id);
        } else if (event.ctrlKey || event.metaKey) {
          this.toggleSelect(id);
        } else {
          this.selectSingle(id);
        }
      },

      // Keyboard Tab focus selects the tile as sole selection (preserves the old
      // single-tile keyboard flow). Suppressed for pointer-driven focus (click
      // handles it) and for programmatic focus after a move (selection already set).
      onSortTileFocus(id: string) {
        if (this._sortPointerDown || this._suppressFocusSelect) return;
        this.selectSingle(id);
      },

      moveSelection(target: MoveTarget) {
        if (!this.sortMode || this.selectedSortIds.length === 0) return;
        const ids = this.assetOrder.map((a) => a.id);
        const { order, leadingId, changed } = computeReorder(ids, this.selectedSortIds, target);
        if (!changed) return;

        this.assetOrder = order.map((id) => ({ id }));
        this.orderDirty = true;
        this.updateDOM();

        if (leadingId) {
          this.$nextTick(() => {
            const el = document.getElementById("sortable-" + leadingId) as HTMLElement | null;
            if (!el) return;
            this._suppressFocusSelect = true;
            el.focus({ preventScroll: true });
            el.scrollIntoView({ block: "nearest", inline: "nearest" });
            this._suppressFocusSelect = false;
          });
        }
      },

      // Per-tile buttons: act on the selection if the clicked tile is part of it,
      // otherwise make it the sole selection first.
      buttonMove(id: string, target: MoveTarget) {
        if (!this.selectedSortIds.includes(id)) this.selectSingle(id);
        this.moveSelection(target);
      },

      updateDOM() {
        // Reorder DOM nodes to match assetOrder. Scroll handling lives in
        // moveSelection (scrollIntoView on the leading tile), so no scrollTop
        // save/restore here — that ambiguous .closest() lookup was the
        // "jumps to top" bug.
        const container = document.getElementById("group-content-" + this.activeGroupID);
        if (!container) return;
        this.assetOrder.forEach((item) => {
          const el = document.querySelector('[data-asset-wrapper="' + item.id + '"]');
          if (el) container.appendChild(el);
        });
      },

      async saveOrder() {
        const payload = { order: this.assetOrder };
        await fetchWithOOB(
          "PUT",
          "/api/deliverables/" +
            this.deliverableID +
            "/groups/" +
            this.activeGroupID +
            "/asset-order",
          payload,
        );

        // Update originalOrder to current state for subsequent reorders/cancels
        this.originalOrder = JSON.parse(JSON.stringify(this.assetOrder));
        this.exitSortMode(true);
      },

      async deleteSelected() {
        if (!(await studioConfirm(this.deleteConfirmMsg))) return;
        // Read the selection AFTER the (async) confirm resolves.
        // @ts-ignore - Alpine globals
        const ids = Alpine.store("selection").array;
        if (ids.length === 0) return;

        await fetchWithOOB("DELETE", "/api/deliverables/" + this.deliverableID + "/assets", {
          asset_ids: ids,
        });

        // @ts-ignore
        Alpine.store("selection").deselectAll();
      },

      refreshContent() {
        if (!this.activeGroupID) return;
        const url =
          "/api/deliverables/" + this.deliverableID + "/groups/" + this.activeGroupID + "/content";
        // @ts-ignore - htmx global
        if (window.htmx) {
          // @ts-ignore
          window.htmx.ajax("GET", url, { target: "#main-content-area", swap: "innerHTML" });
        }
      },

      async bulkRenameAssets() {
        const patternEl = document.getElementById("bulk-rename-pattern") as HTMLInputElement;
        const startIdxEl = document.getElementById("bulk-rename-start-index") as HTMLInputElement;
        if (!patternEl || !startIdxEl) return;

        const pattern = patternEl.value;
        const startIdx = parseInt(startIdxEl.value) || 1;
        // @ts-ignore
        const ids = Alpine.store("selection").array;

        await fetchWithOOB(
          "POST",
          "/api/deliverables/" +
            this.deliverableID +
            "/groups/" +
            this.activeGroupID +
            "/assets/bulk-rename",
          {
            asset_ids: ids,
            pattern: pattern,
            start_index: startIdx,
          },
        );

        // @ts-ignore
        Alpine.store("selection").deselectAll();
        (document.getElementById("bulk-rename-modal") as any)?.close();
      },

      openRenameModal(id: string, filename: string) {
        this.renameAssetID = id;

        // Extract extension
        const lastDotIndex = filename.lastIndexOf(".");
        if (lastDotIndex > 0) {
          this.renameAssetFilename = filename.substring(0, lastDotIndex);
          // @ts-ignore
          this.renameAssetExtension = filename.substring(lastDotIndex);
        } else {
          this.renameAssetFilename = filename;
          // @ts-ignore
          this.renameAssetExtension = "";
        }

        const input = document.getElementById("single-rename-filename") as HTMLInputElement;
        // @ts-ignore
        if (input) input.value = this.renameAssetFilename;
        (document.getElementById("single-rename-modal") as any)?.showModal();
      },

      async renameAsset() {
        const assetID = this.renameAssetID;
        const input = document.getElementById("single-rename-filename") as HTMLInputElement;
        if (!input || !assetID) return;

        // Reconstruct full filename
        // @ts-ignore
        const newFilename = input.value + this.renameAssetExtension;

        await fetchWithOOB(
          "PATCH",
          "/api/deliverables/" +
            this.deliverableID +
            "/groups/" +
            this.activeGroupID +
            "/assets/" +
            assetID +
            "/rename",
          {
            filename: newFilename,
          },
        );

        (document.getElementById("single-rename-modal") as any)?.close();
      },

      linkedModalContext: { groupID: "" as string, deliverableID: "" as string },

      openLinkedModal(gid: string, did: string) {
        this.linkedModalContext = { groupID: gid, deliverableID: did };
        // @ts-ignore - htmx global
        (window as any).htmx.ajax(
          "GET",
          "/api/deliverables/" + did + "/groups/" + gid + "/links",
          "#linked-deliverables-modal-body",
        );
        (document.getElementById("linked-deliverables-modal") as HTMLDialogElement).showModal();
      },

      refreshLinkedModal() {
        const c = this.linkedModalContext;
        if (!c.groupID || !c.deliverableID) return;
        const dlg = document.getElementById(
          "linked-deliverables-modal",
        ) as HTMLDialogElement | null;
        if (!dlg || !dlg.open) {
          this.linkedModalContext = { groupID: "", deliverableID: "" };
          return;
        }
        // @ts-ignore - htmx global
        (window as any).htmx.ajax(
          "GET",
          "/api/deliverables/" + c.deliverableID + "/groups/" + c.groupID + "/links",
          "#linked-deliverables-modal-body",
        );
      },

      openReplaceModal(
        deliverableID: string,
        groupID: string,
        id: string,
        filename: string,
        childCount: number,
      ) {
        window.dispatchEvent(
          new CustomEvent("open-replace-modal", {
            detail: { deliverableID, groupID, id, filename, childCount },
          }),
        );
      },
    }),
  );

  Alpine.data(
    "deliverableGroupItem",
    (
      groupID: string,
      initialName: string,
      deliverableID: string,
      includeInArchive: boolean,
      visibleToClient: boolean,
      linkedDeliverables: number = 1,
      renameConfirmMsg: string = "",
    ) => alpineData({
      groupID: groupID,
      deliverableID: deliverableID,
      editingGroup: false,
      groupName: initialName,
      originalName: initialName,
      isLoading: false,
      includeInArchive: includeInArchive,
      visibleToClient: visibleToClient,
      linkedDeliverables: linkedDeliverables,
      renameConfirmMsg: renameConfirmMsg,

      startEdit() {
        this.editingGroup = true;
        this.$nextTick(() => {
          const input = this.$refs.editInput as HTMLInputElement;
          if (input) {
            input.focus();
            input.select();
          }
        });
      },

      cancelEdit() {
        this.editingGroup = false;
        this.groupName = this.originalName;
      },

      async saveEdit() {
        const name = this.groupName.trim();
        if (!name || name === this.originalName) {
          this.cancelEdit();
          return;
        }

        // Warn when renaming a shared group (appears in multiple deliverables)
        if (this.linkedDeliverables > 1 && this.renameConfirmMsg) {
          // Themed confirm is async and takes focus, so the input's
          // @blur="cancelEdit()" has already reverted groupName by the time it
          // resolves — `name` was captured above; restore it on confirm.
          if (!(await studioConfirm(this.renameConfirmMsg))) {
            this.cancelEdit();
            return;
          }
          this.groupName = name;
        }

        this.isLoading = true;
        try {
          const url = `/api/deliverables/${this.deliverableID}/groups/${this.groupID}/rename`;
          const resp = await fetchWithOOB("PATCH", url, { name });
          if (resp.ok) {
            this.originalName = name;
            this.editingGroup = false;
          } else {
            this.cancelEdit();
          }
        } catch (e) {
          console.error("[deliverableGroupItem] Failed to rename group:", e);
          this.cancelEdit();
        } finally {
          this.isLoading = false;
        }
      },

      async toggleVisibility(type: "archive" | "client") {
        const oldValue = type === "archive" ? this.includeInArchive : this.visibleToClient;
        const newValue = !oldValue;

        // Optimistic update
        if (type === "archive") this.includeInArchive = newValue;
        else this.visibleToClient = newValue;

        try {
          const url = `/api/deliverables/${this.deliverableID}/groups/${this.groupID}/visibility`;
          const resp = await fetchWithOOB("PATCH", url, {
            include_in_archive: this.includeInArchive,
            visible_to_client: this.visibleToClient,
          });

          if (!resp.ok) {
            // Rollback on error
            if (type === "archive") this.includeInArchive = oldValue;
            else this.visibleToClient = oldValue;
          }
        } catch (e) {
          console.error("[deliverableGroupItem] Failed to toggle visibility:", e);
          // Rollback on error
          if (type === "archive") this.includeInArchive = oldValue;
          else this.visibleToClient = oldValue;
        }
      },
    }),
  );
};
